
/* ==========================================
   A2Z WORK SHOWCASE CAROUSEL
   Native JavaScript - No dependencies
   ========================================== */

(() => {
  const selector = '[data-a2z-work]';
  const instances = new WeakMap();

  function init(section) {
    if (instances.has(section)) return;

    const track = section.querySelector('[data-work-track]');
    const prev = section.querySelector('[data-work-prev]');
    const next = section.querySelector('[data-work-next]');
    const dots = section.querySelector('[data-work-dots]');

    if (!track || !prev || !next || !dots) return;

    const cards = Array.from(
      track.querySelectorAll('.a2z-work__card')
    );

    let positions = [];
    let activeIndex = 0;
    let frame = null;

    const reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    );

    function getPositions() {
      const maxScroll = Math.max(
        0,
        track.scrollWidth - track.clientWidth
      );

      const values = cards.map(card =>
        Math.min(card.offsetLeft - track.offsetLeft, maxScroll)
      );

      return values.filter(
        (value, index, array) =>
          index === 0 || value > array[index - 1] + 1
      );
    }

    function nearestIndex() {
      const current = track.scrollLeft;
      let nearest = 0;
      let distance = Infinity;

      positions.forEach((position, index) => {
        const difference = Math.abs(position - current);

        if (difference < distance) {
          distance = difference;
          nearest = index;
        }
      });

      return nearest;
    }

    function updateControls() {
      activeIndex = nearestIndex();

      prev.disabled = activeIndex === 0;
      next.disabled = activeIndex >= positions.length - 1;

      dots.querySelectorAll('button').forEach((dot, index) => {
        dot.setAttribute(
          'aria-current',
          String(index === activeIndex)
        );
      });
    }

    function goTo(index) {
      if (!positions.length) return;

      const target = Math.max(
        0,
        Math.min(index, positions.length - 1)
      );

      track.scrollTo({
        left: positions[target],
        behavior: reducedMotion.matches ? 'instant' : 'smooth'
      });
    }

    function rebuild() {
      positions = getPositions();

      const scrollable = positions.length > 1;

      section.classList.toggle(
        'a2z-work--scrollable',
        scrollable
      );

      dots.replaceChildren();

      if (scrollable) {
        positions.forEach((position, index) => {
          const dot = document.createElement('button');

          dot.type = 'button';
          dot.className = 'a2z-work__dot';
          dot.setAttribute(
            'aria-label',
            `Go to project position ${index + 1}`
          );

          dot.addEventListener('click', () => goTo(index));
          dots.appendChild(dot);
        });
      }

      updateControls();
    }

    function onScroll() {
      if (frame !== null) {
        cancelAnimationFrame(frame);
      }

      frame = requestAnimationFrame(() => {
        updateControls();
        frame = null;
      });
    }

    prev.addEventListener('click', () => {
      goTo(nearestIndex() - 1);
    });

    next.addEventListener('click', () => {
      goTo(nearestIndex() + 1);
    });

    track.addEventListener('scroll', onScroll, {
      passive: true
    });

    const observer = new ResizeObserver(rebuild);
    observer.observe(track);

    instances.set(section, {
      destroy() {
        observer.disconnect();

        if (frame !== null) {
          cancelAnimationFrame(frame);
        }
      }
    });

    rebuild();
  }

  function initAll(root = document) {
    if (root.matches?.(selector)) {
      init(root);
    }

    root.querySelectorAll(selector).forEach(init);
  }

  function start() {
    initAll();

    document.addEventListener('shopify:section:load', event => {
      initAll(event.target);
    });

    document.addEventListener('shopify:section:unload', event => {
      const sections = [
        ...(event.target.matches?.(selector)
          ? [event.target]
          : []),
        ...event.target.querySelectorAll(selector)
      ];

      sections.forEach(section => {
        instances.get(section)?.destroy();
        instances.delete(section);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, {
      once: true
    });
  } else {
    start();
  }
})();
